import React, { useState, useRef, useEffect } from 'react';
import {
    Layout, Card, Button, Spin, Alert, Modal, message,
    Typography, Flex, Input, Space
} from 'antd';
import {
    CheckOutlined, CloseOutlined, ExclamationCircleOutlined
} from '@ant-design/icons';
import { Document, Page, pdfjs } from 'react-pdf';
import * as publicActions from 'store/actions/PublicActions';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

const { Content, Header } = Layout;
const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

// Set up PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const PublicApproverView = ({ document, recipient, fields, other_recipients, token, onComplete }) => {
    const pdfContainerRef = useRef(null);

    // State
    const [submitting, setSubmitting] = useState(false);
    const [pdfUrl, setPdfUrl] = useState(null);
    const [numPages, setNumPages] = useState(0);
    const [zoomLevel, setZoomLevel] = useState(1.0);
    const [, setPdfLoading] = useState(true);
    const [pdfError, setPdfError] = useState(null);
    const [rejectModalOpen, setRejectModalOpen] = useState(false);
    const [approveModalOpen, setApproveModalOpen] = useState(false);
    const [comments, setComments] = useState('');
    const [rejectReason, setRejectReason] = useState('');

    // Check if all signers have signed
    const pendingSigners = (other_recipients || []).filter(
        r => r.recipient_role === 'SIGNER' && !r.has_signed
    );
    const allSignersSigned = pendingSigners.length === 0;

    // Initialize PDF URL
    useEffect(() => {
        if (document?.signed_document_file) {
            setPdfUrl(document.signed_document_file);
        } else if (document?.document_file_url || document?.document_file) {
            setPdfUrl(document.document_file_url || document.document_file);
        }
    }, [document]);

    const onDocumentLoadSuccess = ({ numPages }) => {
        setNumPages(numPages);
        setPdfLoading(false);
        setPdfError(null);
    };

    const onDocumentLoadError = (error) => {
        console.error("PDF load error:", error);
        setPdfLoading(false);
        setPdfError(error);
        message.error("Failed to load PDF document");
    };

    // Handle Approve
    const handleApprove = async () => {
        setSubmitting(true);
        try {
            const response = await publicActions.submitPublicApproval(token, {
                approved: true,
                comments: comments
            });

            if (response?.success !== false) {
                message.success('Document approved successfully!');
                onComplete?.('approved');
            } else {
                message.error(response?.error || 'Failed to approve document');
            }
        } catch (error) {
            console.error('Approval error:', error);
            message.error('Failed to approve document. Please try again.');
        } finally {
            setSubmitting(false);
            setApproveModalOpen(false);
        }
    };

    // Handle Reject
    const handleReject = async () => {
        if (!rejectReason.trim()) {
            message.warning('Please provide a reason for rejection');
            return;
        }

        setSubmitting(true);
        try {
            const response = await publicActions.submitPublicRejection(token, {
                reason: rejectReason,
                comments: comments
            });

            if (response?.success !== false) {
                message.success('Document rejected');
                onComplete?.('rejected');
            } else {
                message.error(response?.error || 'Failed to reject document');
            }
        } catch (error) {
            console.error('Rejection error:', error);
            message.error('Failed to reject document. Please try again.');
        } finally {
            setSubmitting(false);
            setRejectModalOpen(false);
        }
    };

    return (
        <Layout style={{ minHeight: '100vh', background: '#f5f5f5' }}>
            {/* Simple Header */}
            <Header style={{
                background: '#fff',
                padding: '0 24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}>
                 <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 0,            // correct way to control spacing
                    }}
                >
                    <Title level={4} style={{ margin: 0 }}>
                        Review & Approve Document
                    </Title>

                    <Text type="secondary" style={{ margin: 0 }}>
                        {document?.title || document?.name}
                    </Text>
                </div>
                <div>
                    <Text style={{ marginRight: 16 }}>
                        Reviewing as: <strong>{recipient?.name}</strong>
                    </Text>
                    <Space>
                        <Button
                            danger
                            icon={<CloseOutlined />}
                            onClick={() => setRejectModalOpen(true)}
                            disabled={!allSignersSigned || submitting}
                        >
                            Reject
                        </Button>
                        <Button
                            type="primary"
                            icon={<CheckOutlined />}
                            onClick={() => setApproveModalOpen(true)}
                            disabled={!allSignersSigned || submitting}
                            style={{ background: '#52c41a', borderColor: '#52c41a' }}
                        >
                            Approve
                        </Button>
                    </Space>
                </div>
            </Header>

            <Content style={{ padding: 24 }}>
                {/* Waiting for signers alert */}
                {!allSignersSigned && (
                    <Alert
                        message="Waiting for Signatures"
                        description={
                            <div>
                                <Paragraph style={{ marginBottom: 8 }}>
                                    This document cannot be approved yet. The following signers must complete their signatures first:
                                </Paragraph>
                                <ul style={{ marginBottom: 0 }}>
                                    {pendingSigners.map((signer, index) => (
                                        <li key={index}>{signer.name}</li>
                                    ))}
                                </ul>
                            </div>
                        }
                        type="warning"
                        showIcon
                        icon={<ExclamationCircleOutlined />}
                        style={{ marginBottom: 16 }}
                    />
                )}

                {allSignersSigned && (
                    <Alert
                        message="All Signatures Collected"
                        description="All required signatures have been collected. You can now review and approve or reject this document."
                        type="success"
                        showIcon
                        style={{ marginBottom: 16 }}
                    />
                )}

                {/* Signers Status */}
                {other_recipients && other_recipients.length > 0 && (
                    <Card title="Signature Status" style={{ marginBottom: 16 }}>
                        <Space direction="vertical" style={{ width: '100%' }}>
                            {other_recipients
                                .filter(r => r.recipient_role === 'SIGNER')
                                .map((signer, index) => (
                                    <div key={index} style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        padding: '8px 0',
                                        borderBottom: '1px solid #f0f0f0'
                                    }}>
                                        <Text>{signer.name}</Text>
                                        {signer.has_signed ? (
                                            <Text type="success">
                                                <CheckOutlined /> Signed
                                            </Text>
                                        ) : (
                                            <Text type="warning">Pending</Text>
                                        )}
                                    </div>
                                ))}
                        </Space>
                    </Card>
                )}

                {/* PDF Preview */}
                <Card title="Document Preview">
                    <div
                        ref={pdfContainerRef}
                        style={{
                            backgroundColor: '#666',
                            padding: 20,
                            borderRadius: 8,
                            maxHeight: 'calc(100vh - 400px)',
                            overflowY: 'auto'
                        }}
                    >
                        <Flex justify="center">
                            {pdfError ? (
                                <div style={{ color: '#fff', padding: 40, textAlign: 'center' }}>
                                    <p>Failed to load PDF</p>
                                </div>
                            ) : pdfUrl ? (
                                <Document
                                    file={pdfUrl}
                                    onLoadSuccess={onDocumentLoadSuccess}
                                    onLoadError={onDocumentLoadError}
                                    loading={<Spin size="large" tip="Loading PDF..." />}
                                >
                                    {numPages > 0 && [...Array(numPages).keys()].map((_, index) => {
                                        const pageNumber = index + 1;
                                        return (
                                            <div key={pageNumber} style={{ marginBottom: 10 }}>
                                                <Page
                                                    pageNumber={pageNumber}
                                                    scale={zoomLevel}
                                                    renderTextLayer={false}
                                                    renderAnnotationLayer={false}
                                                />
                                                <div style={{
                                                    textAlign: 'center',
                                                    backgroundColor: '#fff',
                                                    padding: '5px'
                                                }}>
                                                    Page {pageNumber} of {numPages}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </Document>
                            ) : (
                                <div style={{ color: '#fff', padding: 40 }}>
                                    No PDF preview available
                                </div>
                            )}
                        </Flex>
                    </div>

                    {/* Zoom Controls */}
                    <Flex justify="center" style={{ marginTop: 16 }} gap={16} align="center">
                        <Button onClick={() => setZoomLevel(prev => Math.max(0.5, prev - 0.25))}>
                            Zoom Out
                        </Button>
                        <span>Zoom: {Math.round(zoomLevel * 100)}%</span>
                        <Button onClick={() => setZoomLevel(prev => Math.min(2, prev + 0.25))}>
                            Zoom In
                        </Button>
                    </Flex>
                </Card>
            </Content>

            {/* Approve Confirmation Modal */}
            <Modal
                title="Approve Document"
                open={approveModalOpen}
                onCancel={() => setApproveModalOpen(false)}
                footer={[
                    <Button key="cancel" onClick={() => setApproveModalOpen(false)}>
                        Cancel
                    </Button>,
                    <Button
                        key="approve"
                        type="primary"
                        icon={<CheckOutlined />}
                        onClick={handleApprove}
                        loading={submitting}
                        style={{ background: '#52c41a', borderColor: '#52c41a' }}
                    >
                        Confirm Approval
                    </Button>
                ]}
            >
                <Paragraph>
                    Are you sure you want to approve this document?
                </Paragraph>
                <div style={{ marginTop: 16 }}>
                    <Text strong>Comments (optional):</Text>
                    <TextArea
                        value={comments}
                        onChange={(e) => setComments(e.target.value)}
                        placeholder="Add any comments..."
                        rows={3}
                        style={{ marginTop: 8 }}
                    />
                </div>
            </Modal>

            {/* Reject Modal */}
            <Modal
                title="Reject Document"
                open={rejectModalOpen}
                onCancel={() => setRejectModalOpen(false)}
                footer={[
                    <Button key="cancel" onClick={() => setRejectModalOpen(false)}>
                        Cancel
                    </Button>,
                    <Button
                        key="reject"
                        danger
                        icon={<CloseOutlined />}
                        onClick={handleReject}
                        loading={submitting}
                    >
                        Confirm Rejection
                    </Button>
                ]}
            >
                <Alert
                    message="This action cannot be undone"
                    description="Rejecting this document will notify all parties and the workflow will be terminated."
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                />
                <div>
                    <Text strong>Reason for rejection (required):</Text>
                    <TextArea
                        value={rejectReason}
                        onChange={(e) => setRejectReason(e.target.value)}
                        placeholder="Please explain why you are rejecting this document..."
                        rows={3}
                        style={{ marginTop: 8 }}
                        status={!rejectReason.trim() ? 'error' : ''}
                    />
                </div>
                <div style={{ marginTop: 16 }}>
                    <Text strong>Additional comments (optional):</Text>
                    <TextArea
                        value={comments}
                        onChange={(e) => setComments(e.target.value)}
                        placeholder="Add any additional comments..."
                        rows={2}
                        style={{ marginTop: 8 }}
                    />
                </div>
            </Modal>
        </Layout>
    );
};

export default PublicApproverView;
